import React from 'react';
import { FaGithub, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { LandingTranslations } from './translations';
import { asIcon } from '../icons';

const XIcon = asIcon(FaXTwitter);
const InstagramIcon = asIcon(FaInstagram);
const LinkedInIcon = asIcon(FaLinkedin);
const YouTubeIcon = asIcon(FaYoutube);
const GitHubIcon = asIcon(FaGithub);

interface LandingFooterProps {
  translations: LandingTranslations;
}

interface FooterLink {
  label: string;
  href: string;
}

const companyLinks: FooterLink[] = [
  { label: 'Impressum', href: 'https://scaientist.eu/impressum' },
  { label: 'Solutions', href: 'https://scaientist.eu/solutions' },
  { label: 'Services', href: 'https://scaientist.eu/services' },
  { label: 'About', href: 'https://scaientist.eu/about' },
  { label: 'FAQs', href: 'https://help.scaientist.eu/' },
];

const solutionLinks: FooterLink[] = [
  { label: 'Cite', href: 'https://cite.scaientist.eu/' },
  { label: 'TechCheck', href: 'https://techcheck.scaientist.eu/' },
  { label: 'SciComic', href: 'https://comic.scaientist.eu/' },
  { label: 'SciBLIND', href: 'https://blind.scaientist.eu/' },
];

const serviceLinks: FooterLink[] = [
  { label: 'Consulting', href: 'https://scaientist.eu/consulting' },
  { label: 'DigiEdu', href: 'https://scaientist.eu/digi-edu' },
  { label: 'SciBOTs', href: 'https://scaientist.eu/scibots' },
  { label: 'SciWEB', href: 'https://scaientist.eu/sciweb' },
  { label: 'Statistical Analysis', href: 'https://scaientist.eu/stats' },
  { label: 'Plagiarism check', href: 'https://scaientist.eu/plagiarism' },
];

const socialLinks = [
  { label: 'X', href: 'https://x.com/scaientist', Icon: XIcon },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/scaientist/',
    Icon: InstagramIcon,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/scaientist/posts/?feedView=all',
    Icon: LinkedInIcon,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@scaientist',
    Icon: YouTubeIcon,
  },
  {
    label: 'GitHub',
    href: 'https://github.com/scaientist',
    Icon: GitHubIcon,
  },
];

const FooterColumn = ({
  title,
  links,
}: {
  title: string;
  links: FooterLink[];
}) => (
  <div className="landing-footer-column">
    <h3>{title}</h3>
    <nav aria-label={title}>
      {links.map((link) => (
        <a key={link.href} href={link.href} target="_blank" rel="noreferrer">
          {link.label}
        </a>
      ))}
    </nav>
  </div>
);

const LandingFooter = ({ translations }: LandingFooterProps) => (
  <footer className="landing-footer">
    <div className="landing-footer-inner">
      <div className="landing-footer-main">
        <div className="landing-footer-brand">
          <a href="https://scaientist.eu/" target="_blank" rel="noreferrer">
            sc<span>ai</span>entist<sup>®</sup>
          </a>
          <small>Meaningful Digital Knowledge</small>
          <p>{translations.footerTagline}</p>
        </div>

        <FooterColumn title={translations.footerCompany} links={companyLinks} />
        <FooterColumn
          title={translations.footerSolutions}
          links={solutionLinks}
        />
        <FooterColumn
          title={translations.footerServices}
          links={serviceLinks}
        />

        <div className="landing-footer-column landing-footer-contact">
          <h3>{translations.footerContact}</h3>
          <a href="mailto:info@scaientist.com">info@scaientist.com</a>
          <div className="landing-footer-socials">
            {socialLinks.map(({ label, href, Icon }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                title={label}
              >
                <Icon />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="landing-footer-bottom">
        <p>
          {translations.footerRights} © {new Date().getFullYear()}{' '}
          SCAIentist®, inovativne digitalne rešitve s pozitivnim družbenim
          vplivom, d.o.o.
        </p>
        <nav aria-label="Legal">
          <a
            href="https://scaientist.eu/privacy-policy/"
            target="_blank"
            rel="noreferrer"
          >
            {translations.footerPrivacy}
          </a>
          <span aria-hidden="true" />
          <a
            href="https://scaientist.eu/terms/"
            target="_blank"
            rel="noreferrer"
          >
            {translations.footerTerms}
          </a>
        </nav>
      </div>
    </div>
  </footer>
);

export default LandingFooter;
